import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customerProductsApi } from '../lib/api';
import { useCurrency } from '../hooks/useCurrency';
import { Globe, Server, HardDrive, ExternalLink, Box, Search, X, CalendarClock } from 'lucide-react';
import clsx from 'clsx';
import Pagination from '../components/Pagination';
import { readPagination } from '../lib/pagination';
import { useTranslation } from 'react-i18next';

interface Product {
  id: string;
  type: string;
  domain?: string;
  username?: string;
  status: string;
  billingCycle: string;
  price: number;
  packageName?: string | null;
  nextDueDate?: string;
  domainManagement?: string | null;
  externalRegistrar?: string | null;
  expiresAt?: string | null;
  autoRenew?: boolean;
  provisioningData?: {
    ip?: string;
    nameservers?: string[];
  };
  package?: {
    id: string;
    name: string;
    slug: string;
    diskMb: number;
    bandwidthMb: number;
    emailAccounts: number;
    databases: number;
  };
  server?: {
    id: string;
    name: string;
    hostname: string;
    cpanelUrl?: string;
  };
}

/** Everything the customer holds under one domain name: hosting, its
 *  registration, or both. */
interface ServiceGroup {
  domain: string;
  items: Product[];
}

/** A resource cPanel reports in its own unit; only the ratio is meaningful. */
interface UsageRatio {
  used: number;
  limit: number | null;
  percent: number | null;
}

interface Usage {
  disk: { usedMb: number | null; limitMb: number | null; percent: number | null };
  bandwidth: UsageRatio | null;
  emailAccounts: UsageRatio | null;
  databases: UsageRatio | null;
  plan: string | null;
  checkedAt: string;
}

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  QUEUED: 'bg-blue-100 text-blue-700',
  PROVISIONING: 'bg-brand-100 text-brand-700',
  ACTIVE: 'bg-green-100 text-green-700',
  LINKED: 'bg-green-100 text-green-700',
  SUSPENDED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-700',
  TERMINATED: 'bg-gray-100 text-gray-700',
};

function formatMb(mb: number): string {
  if (mb >= 1024 * 1024) return `${(mb / 1024 / 1024).toFixed(1)} TB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB`;
  return `${Math.round(mb)} MB`;
}

/** Package quotas store -1 for unlimited. */
function formatQuota(value: number): string {
  return value === -1 ? 'Unlimited' : formatMb(value);
}

function formatCount(value: number): string {
  return value === -1 ? 'Unlimited' : String(value);
}

/** Red once the resource is nearly gone, so a full disk is visible at a glance. */
function barTone(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-brand-500';
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [expiredOnly, setExpiredOnly] = useState(false);

  // Debounced, so typing a domain does not fire a query per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ['customer-products', page, search, expiredOnly],
    queryFn: () =>
      customerProductsApi.list({ page, search: search || undefined, expired: expiredOnly || undefined }),
    placeholderData: (previous) => previous,
  });

  const pageInfo = readPagination(data);

  const groups = (data?.data as ServiceGroup[]) || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('products.title')}</h1>
          <p className="text-gray-500">{t('products.subtitle')}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('products.searchDomain')}
            className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              aria-label={t('products.clearSearch')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <label className="flex items-center gap-2 text-sm text-gray-600 mt-2">
            <input
              type="checkbox"
              checked={expiredOnly}
              onChange={(e) => { setExpiredOnly(e.target.checked); setPage(1); }}
            />
            {t('products.expiredOnly')}
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-2 text-gray-500">{t('products.loading')}</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Box className="h-12 w-12 mx-auto text-gray-400" />
          <h3 className="mt-4 text-lg font-medium">
            {search ? 'Nothing matches that search' : 'No products yet'}
          </h3>
          <p className="mt-2 text-gray-500">
            {search ? 'Try a different domain or username.' : 'Contact us to get started with hosting!'}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3 items-start">
          {groups.map((group) => (
            <ServiceGroupCard key={group.domain} group={group} />
          ))}
        </div>
      )}
      {pageInfo && <Pagination {...pageInfo} onChange={setPage} />}
    </div>
  );
}

function ServiceGroupCard({ group }: { group: ServiceGroup }) {
  const hosting = group.items.find((item) => item.type !== 'DOMAIN');
  const registration = group.items.find((item) => item.type === 'DOMAIN');
  const canOpenCpanel =
    hosting?.username && (hosting.status === 'ACTIVE' || hosting.status === 'LINKED');

  const handleOpenCPanel = async () => {
    if (!hosting) return;
    try {
      const response = await customerProductsApi.getSso(hosting.id);
      const data = response.data as { url: string };
      window.open(data.url, '_blank');
    } catch (error) {
      alert(`Failed to open cPanel: ${(error as Error).message}`);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-brand-100 rounded-lg shrink-0">
            <Globe className="h-5 w-5 text-brand-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-lg truncate" title={group.domain}>
              {group.domain}
            </h3>
            <p className="text-sm text-gray-500 truncate">
              {hosting ? hosting.packageName || 'Hosting' : 'Domain registration'}
            </p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              {/* One chip per thing the customer holds under this name, each
                  carrying its own status: hosting can be suspended while the
                  registration is perfectly fine. */}
              {hosting && <PartBadge label="Hosting" status={hosting.status} />}
              {registration && <PartBadge label="Domain" status={registration.status} />}
              {canOpenCpanel && (
                <button
                  onClick={handleOpenCPanel}
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  cPanel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stacked, not columned: the card is a third of a row wide now, so the
          sections read down it and the label of a pair stays beside its value. */}
      <div className="p-4 space-y-5">
        {hosting && <HostingDetails hosting={hosting} />}
        {registration && <RegistrationDetails registration={registration} />}
      </div>

      {/* Billing — a line per item, because hosting and the domain renew on
          their own dates and for their own money. */}
      <div className="px-4 py-3 bg-gray-50 space-y-2 text-sm">
        {group.items.map((item) => (
          <BillingLine key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function PartBadge({ label, status }: { label: string; status: string }) {
  return (
    <span
      className={clsx(
        'px-3 py-1 text-sm font-medium rounded-full',
        statusColors[status] || 'bg-gray-100 text-gray-700'
      )}
      title={status}
    >
      {label}
      {status !== 'ACTIVE' && status !== 'LINKED' && ` · ${status.toLowerCase()}`}
    </span>
  );
}

function BillingLine({ item }: { item: Product }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const isDomain = item.type === 'DOMAIN';
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState('');

  /**
   * Pay for the next period.
   *
   * Raises the renewal order and its invoice, then goes straight to the
   * invoice: the customer asked to renew, so what they want next is the way to
   * pay. Nothing is extended until that invoice is settled.
   */
  const renew = async () => {
    setRenewing(true);
    setError('');
    try {
      const response = await customerProductsApi.renew(item.id);
      const raised = (response as any).data as { id: string; invoiceId?: string };
      navigate(raised.invoiceId ? `/invoices/${raised.invoiceId}` : '/invoices');
    } catch (err) {
      setError((err as Error).message || t('products.renewFailed'));
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <span className="text-gray-500">{isDomain ? 'Domain' : 'Hosting'}</span>
        <button
          type="button"
          onClick={renew}
          disabled={renewing}
          className="ml-3 text-brand-600 hover:text-brand-800 font-medium disabled:opacity-50"
        >
          {renewing ? t('products.renewing') : t('products.renew')}
        </button>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
      {/* The date sits under the money rather than beside it: one of the two
          lines often has no due date, and side by side that pushed its price
          out of line with the other's. */}
      <div className="text-right">
        <div className="font-medium text-gray-900">
          {item.price > 0
            ? `${formatPrice(item.price)}/${item.billingCycle.toLowerCase()}`
            : isDomain
              ? 'Ask us'
              : 'Included'}
        </div>
        {item.nextDueDate && (
          <div className="text-xs text-gray-500">
            Next due {new Date(item.nextDueDate).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}

function RegistrationDetails({ registration }: { registration: Product }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
        <CalendarClock className="h-4 w-4" />
        {t('products.registration')}
      </h4>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">{t('products.managedBy')}</span>
          <span>
            {registration.domainManagement === 'INTERNAL'
              ? 'Us'
              : registration.externalRegistrar || 'Another registrar'}
          </span>
        </div>
        {registration.expiresAt && (
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">{t('products.expires')}</span>
            <span>{new Date(registration.expiresAt).toLocaleDateString()}</span>
          </div>
        )}
        <div className="flex justify-between gap-4">
          <span className="text-gray-500">{t('products.autoRenew')}</span>
          <span>{registration.autoRenew ? 'On' : 'Off'}</span>
        </div>
      </div>
    </div>
  );
}

function HostingDetails({ hosting }: { hosting: Product }) {
  const { t } = useTranslation();
  const hasCpanel = Boolean(hosting.username && hosting.server);

  // Live figures, asked for per card. The list itself never waits on WHM.
  const { data: usageResponse, isLoading: usageLoading, isError: usageError } = useQuery({
    queryKey: ['customer-service-usage', hosting.id],
    queryFn: () => customerProductsApi.usage(hosting.id),
    enabled: hasCpanel,
    staleTime: 60 * 1000,
    retry: false,
  });
  const usage = usageResponse?.data as Usage | undefined;

  return (
    <>
      <div className="space-y-3">
        <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
          <Server className="h-4 w-4" />
          {t('products.serverDetails')}
        </h4>
        <div className="space-y-2 text-sm">
          {hosting.username && (
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">{t('products.username')}</span>
              <span className="font-mono truncate">{hosting.username}</span>
            </div>
          )}
          {hosting.server && (
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">{t('products.server')}</span>
              <span className="truncate">{hosting.server.name}</span>
            </div>
          )}
          {hosting.provisioningData?.ip && (
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">{t('products.ipAddress')}</span>
              <span className="font-mono truncate">{hosting.provisioningData.ip}</span>
            </div>
          )}
          {hosting.provisioningData?.nameservers && hosting.provisioningData.nameservers.length > 0 && (
            <div>
              <span className="text-gray-500">{t('products.nameservers')}</span>
              <div className="mt-1 space-y-1">
                {hosting.provisioningData.nameservers.map((ns, i) => (
                  <div key={i} className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                    {ns}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Resources — what the account is using, not what the plan allows.
          Bars are drawn only where there is a limit to draw one against. */}
      {(hasCpanel || hosting.package) && (
        <div className="space-y-3">
          <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            {t('products.resources')}
          </h4>

          {hasCpanel && usageLoading && (
            <p className="text-sm text-gray-400">{t('products.usageLoading')}</p>
          )}

          {hasCpanel && usageError && (
            <p className="text-sm text-gray-400">{t('products.usageUnavailable')}</p>
          )}

          {usage && (
            <div className="space-y-3">
              <UsageRow
                label="Disk Space"
                value={
                  usage.disk.usedMb === null
                    ? 'Unknown'
                    : usage.disk.limitMb
                      ? `${formatMb(usage.disk.usedMb)} of ${formatMb(usage.disk.limitMb)}`
                      : `${formatMb(usage.disk.usedMb)} used · Unlimited`
                }
                percent={usage.disk.percent}
              />
              <UsageRow
                label="Bandwidth"
                value={
                  !usage.bandwidth
                    ? 'Unknown'
                    : usage.bandwidth.percent !== null
                      ? `${usage.bandwidth.percent}% of this month's allowance`
                      : 'Unlimited'
                }
                percent={usage.bandwidth?.percent ?? null}
              />
              <UsageRow
                label="Email Accounts"
                value={
                  !usage.emailAccounts
                    ? 'Unknown'
                    : usage.emailAccounts.limit
                      ? `${usage.emailAccounts.used} of ${usage.emailAccounts.limit}`
                      : `${usage.emailAccounts.used} · Unlimited`
                }
                percent={usage.emailAccounts?.percent ?? null}
              />
              {usage.databases && (
                <UsageRow
                  label="Databases"
                  value={
                    usage.databases.limit
                      ? `${usage.databases.used} of ${usage.databases.limit}`
                      : `${usage.databases.used} · Unlimited`
                  }
                  percent={usage.databases.percent}
                />
              )}
            </div>
          )}

          {/* No cPanel account to measure — the plan's allowances are all
              there is to say. */}
          {!usage && !usageLoading && hosting.package && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('products.diskSpace')}</span>
                <span>{formatQuota(hosting.package.diskMb)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('products.bandwidth')}</span>
                <span>{formatQuota(hosting.package.bandwidthMb)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('products.emailAccounts')}</span>
                <span>{formatCount(hosting.package.emailAccounts)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function UsageRow({
  label,
  value,
  percent,
}: {
  label: string;
  value: string;
  percent: number | null;
}) {
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-500">{label}</span>
        <span>{value}</span>
      </div>
      {percent !== null && (
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={clsx('h-full rounded-full transition-all', barTone(percent))}
            style={{ width: `${Math.max(percent, 1)}%` }}
          />
        </div>
      )}
    </div>
  );
}
